
import { mapState, mapGetters } from 'vuex'
import eventBus from '~/event-bus'
import tableVirtualScrollMixin from '~/mixins/table-virtual-scroll'
const filtersUtils = require('~/assets/filters-utils')

export default {
  mixins: [tableVirtualScrollMixin],
  data: () => ({
    data: {},
    query: null,
    pagination: {
      page: 1,
      itemsPerPage: 20,
      sortBy: [null],
      sortDesc: [false]
    },
    loading: false,
    filters: [],
    lastParams: null,
    selectedCols: [],
    ready: false,
    fixedCol: null,
    noInteraction: false,
    conceptsFilters: {}
  }),
  computed: {
    ...mapState(['vocabulary']),
    ...mapState('dataset', ['dataset']),
    ...mapGetters('dataset', ['resourceUrl', 'qMode', 'imageField']),
    displayMode: {
      get () {
        if (this.$route.query.display) return this.$route.query.display
        if (this.$vuetify.breakpoint.smAndDown) return 'list'
        return 'table'
      },
      async set (value) {
        const url = new URL(window.location.href)
        url.searchParams.set('display', value)
        window.location.href = url.href
      }
    },
    lineHeight () {
      return this.displayMode === 'table-dense' ? 28 : 40
    },
    headers () {
      const fieldsHeaders = this.dataset.schema
        .filter(field => !field['x-calculated'])
        .map(field => ({
          text: field.title || field['x-originalName'] || field.key,
          value: field.key,
          sortable:
              (!field['x-capabilities'] || field['x-capabilities'].values !== false) && (
                (field.type === 'string' && field['x-refersTo'] !== 'https://purl.org/geojson/vocab#geometry') ||
                field.type === 'number' ||
                field.type === 'integer'
              ),
          tooltip: field.description || (field['x-refersTo'] && this.vocabulary && this.vocabulary[field['x-refersTo']] && this.vocabulary[field['x-refersTo']].description),
          field
        }))

      if (this.imageField) {
        fieldsHeaders.unshift({ text: '', value: '_thumbnail' })
      }
      if (this.dataset.bbox && !this.noInteraction) {
        fieldsHeaders.unshift({ text: '', value: '_map_preview' })
      }
      return fieldsHeaders
    },
    cols () {
      return this.headers.filter(h => !!h.field).map(h => h.value)
    },
    selectedHeaders () {
      return this.headers.filter(h => (!h.field || !this.selectedCols.length || this.selectedCols.includes(h.value)) && h.value !== this.fixedCol)
    },
    truncate () {
      if (this.displayMode === 'list') return 200
      const minTruncate = this.$vuetify.breakpoint.mdAndUp ? 50 : 40
      const maxTruncate = 200
      const estimatedTruncate = this.windowWidth / this.selectedHeaders.length / 8 // 8px is about a char's width
      const roundedTruncate = Math.round(estimatedTruncate / 20) * 20
      return Math.min(maxTruncate, Math.max(minTruncate, roundedTruncate))
    },
    params () {
      const params = {
        ...this.conceptsFilters,
        size: this.pagination.itemsPerPage,
        page: this.pagination.page,
        q_mode: this.qMode,
        truncate: this.truncate
      }
      if (this.imageField) params.thumbnail = '40x40'
      if (this.pagination.sortBy[0]) {
        params.sort = (this.pagination.sortDesc[0] ? '-' : '') + this.pagination.sortBy[0]
      }
      if (this.query) params.q = this.query
      if (this.filters.length) {
        try {
          filtersUtils.writeQueryParams(this.dataset, this.filters, params)
        } catch (error) {
          // eslint-disable-next-line vue/no-async-in-computed-properties
          this.$nextTick(() => eventBus.$emit('notification', { error }))
        }
      }
      if (this.dataset.finalizedAt) params.finalizedAt = this.dataset.finalizedAt
      // if (this.displayMode === 'list') params.html = true

      return params
    },
    downloadParams () {
      if (this.selectedCols.length === 0) return this.params
      return { ...this.params, select: this.selectedCols.join(',') }
    },
    extensionHeight () {
      let height = 0
      if (this.$vuetify.breakpoint.smAndDown && this.filters.length) height += 40
      return height
    },
    tableHeight () {
      let height = this.windowHeight
      if (!this.noInteraction) height -= 48 // app bar
      height -= this.extensionHeight
      height -= 48 // fixed header
      height -= 1 // header border
      return height
    },
    filtersWidth () {
      let width = this.windowWidth
      width -= 32 // app bar padding
      width -= 80 // number of lines
      width -= 186 // search bar
      width -= 44 // select display
      width -= 44 // select cols
      width -= 44 // download
      return width
    },
    fixedHeader () {
      return this.fixedCol && this.headers.find(h => h.value === this.fixedCol)
    },
    showTable () {
      return (this.displayMode === 'table' || this.displayMode === 'table-dense') && this.headerWidthsAdjusted
    }
  },
  watch: {
    'dataset.schema' () {
      this.refresh(true)
    },
    pagination: {
      handler () {
        this.refresh(false)
      },
      deep: true
    },
    filters: {
      handler () {
        this.refresh(true)
      },
      deep: true
    },
    selectedCols: {
      async handler () {
        this.writeQueryParams()
        this.headerWidths = {}
      },
      deep: true
    },
    'data.results' () {
      if (this.data.results && (this.data.results.length * this.lineHeight) < this.windowHeight) {
        this.fetchMore()
      }
    },
    showTable (value) {
      if (value) this.watchTableScroll()
    },
    '$route.query' () {
      this.readQueryParams()
    }
  },
  async mounted () {
    this.readQueryParams()
    this.refresh()
  },
  methods: {
    async refresh (resetPagination) {
      this.writeQueryParams()

      if (resetPagination) {
        this.pagination.page = 1
        const goToOpts = { duration: 0 }
        if (this.displayMode === 'list') {
          this.$vuetify.goTo(0, goToOpts)
        } else {
          if (this._tableWrapper) this._tableWrapper.scrollTop = 0
          this.scrollTop = 0
        }
      }

      // prevent triggering multiple times the same request
      const paramsStr = JSON.stringify(this.params)
      if (paramsStr === this.lastParams) return
      this.lastParams = paramsStr

      // this.data = {}
      this.loading = true
      try {
        this.data = await this.$axios.$get(this.resourceUrl + '/lines', { params: this.params })
      } catch (error) {
        eventBus.$emit('notification', { error, msg: 'Erreur pendant la récupération des données' })
      }
      this.loading = false
    },
    async fetchMore (entries, observer, isIntersecting) {
      if (!this.data.next || this.loading || isIntersecting === false) return
      this.loading = true
      try {
        const nextUrl = new URL(this.data.next)
        const nextData = await this.$axios.$get(nextUrl.href)
        this.data.next = nextData.next
        this.data.results = this.data.results.concat(nextData.results)
      } catch (error) {
        eventBus.$emit('notification', { error, msg: 'Erreur pendant la récupération des données' })
      }
      this.loading = false
    },
    addFilter (key, filter) {
      if (typeof filter !== 'object') filter = { type: 'in', values: [filter] }
      filter.field = this.dataset.schema.find(f => f.key === key)
      this.filters = this.filters.filter(f => !(f.field.key === key))
      if (filter.type === 'in' && filter.values.length === 0) return
      this.filters.push(filter)
    },
    readQueryParams () {
      const query = this.$route.query
      if (query.cols) this.selectedCols = query.cols.split(',')
      if (query['hide-cols']) {
        const hiddenCols = query['hide-cols'].split(',')
        this.selectedCols = this.cols.filter(col => !hiddenCols.includes(col))
      }
      if (query.fixed) this.fixedCol = query.fixed
      if (query.q) this.query = query.q
      if (query.sort) {
        const [sortBy, sortDesc] = query.sort.split(':')
        this.$set(this.pagination.sortBy, 0, sortBy)
        this.$set(this.pagination.sortDesc, 0, sortDesc === '-1')
      }
      this.noInteraction = query.interaction === 'false' || query.interaction === '0'
      this.filters = filtersUtils.readQueryParams(query, this.dataset)
      this.conceptsFilters = Object.keys(query)
        .filter(key => key.startsWith('_c_'))
        .reduce((a, key) => { a[key] = query[key]; return a }, {})
    },
    writeQueryParams () {
      const query = { ...this.$route.query }

      if (this.selectedCols.length) {
        if (this.selectedCols.length > this.cols.length / 2) {
          query['hide-cols'] = this.cols.filter(col => !this.selectedCols.includes(col)).join(',')
          delete query.cols
        } else {
          query.cols = this.selectedCols.join(',')
          delete query['hide-cols']
        }
      } else {
        delete query.cols
        delete query['hide-cols']
      }

      if (this.fixedCol) query.fixed = this.fixedCol
      else delete query.fixed

      if (this.query) query.q = this.query
      else delete query.q

      filtersUtils.writeQueryParams(this.dataset, this.filters, query, true)

      if (this.pagination.sortBy && this.pagination.sortBy[0]) {
        query.sort = this.pagination.sortBy[0] + ':' + (this.pagination.sortDesc[0] ? '-1' : '1')
      } else {
        delete query.sort
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query })
      }
    },
    hideHeader (header) {
      if (!this.selectedCols.length) this.selectedCols = this.cols
      this.selectedCols = this.selectedCols.filter(sc => sc !== header.value)
    }
  }
}
